/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { getApiUrl } from "constants/domain"
import { createReducer } from "store/create-reducer"
import { GetIncidentResponse } from "types/api/incidents"
import update from "immutability-helper"
import { AxiosAction } from "types/api"
import { getActionFail, getActionSuccess } from "./utils/actionUtils"
import { IncidentsDrawerState } from "./incidentsDrawer.type"

const NAMESPACE = "INCIDENTS_DRAWER"

export const actions = {
  FETCH_INCIDENT_BY_ORDER_ID: `${NAMESPACE}/FETCH_INCIDENT_BY_ORDER_ID`,
  CLEAR_INCIDENT_DETAIL: `${NAMESPACE}/CLEAR_INCIDENT_DETAIL`,
}

const apiUrl = getApiUrl()

export const actionCreator = {
  getIncident({
    merchantID,
    ...params
  }: {
    incidentID: string
    merchantID: string
  }) {
    return {
      type: actions.FETCH_INCIDENT_BY_ORDER_ID,
      showError: true,
      payload: {
        request: {
          url: `${apiUrl}/food/merchant/v1/merchant/incident?${new URLSearchParams(
            params,
          ).toString()}`,
          headers: {
            merchantID,
          },
        },
      },
    }
  },
  clearIncidentDetail() {
    return {
      type: actions.CLEAR_INCIDENT_DETAIL,
    }
  },
}

export const initialState = (): IncidentsDrawerState => ({
  detail: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
})

export type ActionTypes = typeof actions
export type ActionCreatorTypeList = ReturnType<
  typeof actionCreator[keyof typeof actionCreator]
>

export const reducer = createReducer<
  IncidentsDrawerState,
  typeof actions[keyof typeof actions],
  ActionCreatorTypeList
>(initialState(), {
  [actions.FETCH_INCIDENT_BY_ORDER_ID]: (state: IncidentsDrawerState) => {
    return update(state, {
      detail: {
        $set: {
          isLoading: true,
          error: undefined,
          data: undefined,
        },
      },
    })
  },
  [actions.CLEAR_INCIDENT_DETAIL]: (state: IncidentsDrawerState) => {
    return update(state, {
      detail: { $set: initialState().detail },
    })
  },
  [getActionSuccess(actions.FETCH_INCIDENT_BY_ORDER_ID)]: (
    state: IncidentsDrawerState,
    action: AxiosAction<GetIncidentResponse>,
  ) => {
    return update(state, {
      detail: {
        data: { $set: action.payload.data },
        isLoading: { $set: false },
        error: { $set: undefined },
      },
    })
  },
  [getActionFail(actions.FETCH_INCIDENT_BY_ORDER_ID)]: (
    state: IncidentsDrawerState,
    action: AxiosAction<any>,
  ) => {
    return update(state, {
      detail: {
        isLoading: { $set: false },
        error: {
          $set:
            action.error.response?.data.message ??
            JSON.stringify(action.error.response?.data || {}),
        },
      },
    })
  },
})
